import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Grid, TextField, TableRow, TableCell, IconButton, Tooltip, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { toast } from 'react-toastify';
import moment from "moment";
import api from '../../../services/api.js';
import { pode, mensagemErro } from '../../../services/acesso';
import PageHeader from '../../../components/PageHeader';
import FilterBar from '../../../components/FilterBar';
import ConfirmDialog from '../../../components/ConfirmDialog';
import TabelaPaginada, { lerPaginacao } from '../../../components/TabelaPaginada';

const POR_PAGINA = 25;
const FILTROS_INICIAIS = { name: "", corporateName: "" };

/**
 * Mailing.
 * Correções: a paginação começava na página 0; os filtros iam colados na URL sem codificação
 * (uma razão social com "&" quebrava a busca); Pesquisar não voltava para a página 1.
 */
export default function ListingMailing() {
  const history = useHistory();
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [aplicados, setAplicados] = useState(FILTROS_INICIAIS);
  const [page, setPage] = useState(1);
  const [dados, setDados] = useState({ linhas: [], total: 0, totalPaginas: 0, porPagina: POR_PAGINA });
  const [loading, setLoading] = useState(true);
  const [excluir, setExcluir] = useState(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    // params: o axios codifica os valores (antes eram concatenados na URL)
    api.get("mailings", {
      params: {
        page,
        pageSize: POR_PAGINA,
        name: aplicados.name,
        corporate_name: aplicados.corporateName,
      }
    })
      .then((result) => { if (ativo) setDados(lerPaginacao(result, POR_PAGINA)); })
      .catch((e) => { if (ativo) toast.error(mensagemErro(e, "Não foi possível carregar os mailings")); })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [page, aplicados, versao]);

  const aplicar = (novos) => {
    setAplicados(novos);
    setPage(1);
    setVersao((v) => v + 1);
  };

  const limpar = () => {
    setFiltros(FILTROS_INICIAIS);
    aplicar(FILTROS_INICIAIS);
  };

  const remover = (campo) => () => {
    const novos = { ...aplicados, [campo]: "" };
    setFiltros(novos);
    aplicar(novos);
  };

  const ativos = [
    aplicados.name && { rotulo: `Mailing: ${aplicados.name}`, remover: remover("name") },
    aplicados.corporateName && { rotulo: `Contém a empresa: ${aplicados.corporateName}`, remover: remover("corporateName") },
  ].filter(Boolean);

  const handleDeleteClick = () => {
    api.delete(`mailing/${excluir.id}`).then(() => {
      toast.success("Mailing excluído");
      setVersao((v) => v + 1);
    }).catch((e) => toast.error(mensagemErro(e, "Não foi possível excluir o mailing")));
  };

  return (
    <Container maxWidth="lg">
      <PageHeader
        titulo="Mailing"
        acoes={pode("editar_clientes") && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} component={Link} to="/mailing/new">
            Novo mailing
          </Button>
        )}
      />

      <FilterBar onPesquisar={() => aplicar(filtros)} onLimpar={limpar} ativos={ativos}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField id="filtro_nome" label="Nome do mailing" value={filtros.name} onChange={(e) => setFiltros({ ...filtros, name: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="filtro_razao"
              label="Contém a empresa (razão social)"
              value={filtros.corporateName}
              onChange={(e) => setFiltros({ ...filtros, corporateName: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </FilterBar>

      <TabelaPaginada
        rotulo="Lista de mailings"
        colunas={[{ titulo: "Nome" }, { titulo: "Criado em" }, { titulo: "Ações", align: "right" }]}
        carregando={loading}
        vazio={dados.linhas.length === 0}
        textoVazio="Nenhum mailing encontrado"
        temFiltro={ativos.length > 0}
        onLimpar={limpar}
        pagina={page}
        totalPaginas={dados.totalPaginas}
        totalRegistros={dados.total}
        porPagina={dados.porPagina}
        onPagina={setPage}
      >
        {dados.linhas.map((row) => (
          <TableRow key={row.id} hover style={{ cursor: "pointer" }} onClick={() => history.push("/mailing/" + row.id + "/edit")}>
            <TableCell style={{ fontWeight: 600 }}>{row.name}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{row.created_at ? moment(row.created_at, "YYYY-MM-DD HH:mm").format("DD/MM/YYYY") : ""}</TableCell>
            <TableCell align="right" style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Abrir mailing">
                <IconButton size="small" component={Link} to={"/mailing/" + row.id + "/edit"} aria-label={`Abrir ${row.name}`}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {pode("excluir") && (
                <Tooltip title="Excluir mailing">
                  <IconButton size="small" aria-label={`Excluir ${row.name}`} onClick={() => setExcluir(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TabelaPaginada>

      <ConfirmDialog
        title="Excluir mailing?"
        open={Boolean(excluir)}
        setOpen={(v) => { if (!v) setExcluir(null); }}
        onConfirm={handleDeleteClick}
      >
        {excluir ? <>O mailing <b>{excluir.name}</b> será excluído. Esta ação não pode ser desfeita.</> : null}
      </ConfirmDialog>
    </Container>
  );
}
